/* eslint-disable consistent-return */
import axios from 'axios';

import apolloClient from '../../../core/backendClient/apolloClient';
import Schemas from './graphql';

/**
 * @param {Client} client
 * @returns {Promise<string | null>}
 */
async function createClient(client) {
  const res = await apolloClient.mutate({
    mutation: Schemas.CREATE_CLIENT,
    variables: { clientInput: { ...client } },
  });
  if (res.data.createClient.code === 201) {
    return res.data.createClient.client.id;
  }
  return null;
}

/**
 * @param {String} email
 * @returns {Promise<string | null>}
 */
async function getClientId(email) {
  const res = await apolloClient.query({
    query: Schemas.GET_CLIENT_ID,
    variables: { email },
  });
  if (res.data.getClient.code === 200) {
    return res.data.getClient.client.id;
  }
  return null;
}

/**
 * @param {String} storeId
 * @returns {Promise<{devolutionPageConfigs: object,
 *                    storeConfigs: object,
 *                    itemCategories: object[],
 *                    itemReasons: object[]} | null>}
 */
async function getDevolutionPageData(storeId) {
  const res = await apolloClient.query({
    query: Schemas.GET_DEVOULUTION_PAGE_DATA,
    variables: { storeId },
  });
  if (
    res.data.devolutionPageConfigs &&
    res.data.devolutionPageConfigs.code === 200 &&
    res.data.storeConfigs &&
    res.data.storeConfigs.code === 200 &&
    res.data.itemCategories &&
    res.data.itemCategories.code === 200 &&
    res.data.itemReasons &&
    res.data.itemReasons.code === 200 &&
    res.data.integrationStatus &&
    res.data.integrationStatus.code === 200
  ) {
    return {
      devolutionPageConfigs: res.data.devolutionPageConfigs.configs,
      storeConfigs: res.data.storeConfigs.storeConfigs,
      itemCategories: res.data.itemCategories.orderItemCategories,
      itemReasons: res.data.itemReasons.returnReasons,
      integrationStatus: res.data.integrationStatus.status,
    };
  }
  return null;
}

/**
 * @param {String} storeId
 * @param {String} vtexOrderNumber
 * @returns {Promise<object[] | null>}
 */
async function getVtexOrderInfo(storeId, vtexOrderNumber) {
  const res = await apolloClient.query({
    query: Schemas.GET_VTEX_ORDER_INFO,
    variables: { encStoreId: storeId, vtexOrderNumber },
  });
  if (res.data.getVtexOrderInfo.code === 200) {
    return res.data.getVtexOrderInfo.items;
  }
  return null;
}

/**
 * @param {String} storeId
 * @param {object} order
 * @returns {Promise<object | null>}
 */
async function createOrder(storeId, order) {
  const res = await apolloClient.mutate({
    mutation: Schemas.SAVE_ORDER_WO_AUTH,
    variables: { encStoreId: storeId, orderInput: { ...order } },
  });
  if (res.data.saveOrderWOAuth.code === 201) {
    return res.data.saveOrderWOAuth;
  }
  return null;
}

async function createOrderWithClientInfo({ storeId, order }) {
  const res = await apolloClient.mutate({
    mutation: Schemas.CREATE_ORDER_WITH_CLIENT_INFO,
    variables: { storeId, order },
  });
  if (res.data.createOrderWithClientInfo.code === 201) {
    return res.data.createOrderWithClientInfo;
  }
  return null;
}

async function getAddressByZipCode(zipCode) {
  const baseUrl = (cep) => `https://viacep.com.br/ws/${cep}/json/`;
  try {
    const response = await axios.get(baseUrl(zipCode));
    return response.data;
  } catch (err) {
    return null;
  }
}

/**
 * @param {String} storeId
 * @param {Number} returnMethodIndex
 * @param {String} boxCode
 * @returns {Promise<{success: true} | {success: false, message: String}>}
 */
async function setOrderReturnMethod({ orderId, returnMethodIndex, boxCode }) {
  // CliqueRetire
  if (returnMethodIndex === 1) {
    if (!boxCode) {
      return {
        success: false,
        message: 'Para prosseguir, selecione uma locker!',
      };
    }
    const res = await apolloClient.mutate({
      mutation: Schemas.SET_CR_RETURN_METHOD,
      variables: { encOrderId: orderId, boxCode },
    });
    if (
      res.data.setCliqueRetireReturnMethod &&
      res.data.setCliqueRetireReturnMethod.code === 200
    ) {
      return { success: true };
    }
    return {
      success: false,
      message: res.data.setCliqueRetireReturnMethod.message,
    };
  }
  if (returnMethodIndex === 2) {
    // Correios
    const res = await apolloClient.mutate({
      mutation: Schemas.SET_CORREIOS_RETURN_METHOD,
      variables: { encOrderId: orderId },
    });
    if (
      res.data.setCorreiosReturnMethod &&
      res.data.setCorreiosReturnMethod.code === 200
    ) {
      return { success: true };
    }
    return {
      success: false,
      message: res.data.setCorreiosReturnMethod.message,
    };
  }
}

/**
 * @param {String} storeId
 * @param {Boolean} choice
 * @returns {Promise<Boolean>}
 */
async function approveOrRejectOrder({ orderId, choice }) {
  const res = await apolloClient.mutate({
    mutation: Schemas.APPROVE_OR_REJECT_ORDER,
    variables: { orderId, choice },
  });
  if (
    res.data.approveOrRejectOrder &&
    res.data.approveOrRejectOrder.code === 200
  ) {
    return true;
  }
  return false;
}

async function getStoreIdByNameId(storeNameId) {
  const res = await apolloClient.query({
    query: Schemas.GET_STORE_ID_BY_STORE_NAME_ID,
    variables: { storeNameId },
  });
  if (res.data.getStoreByStoreNameId.code === 200) {
    return res.data.getStoreByStoreNameId.store.id;
  }
  return null;
}

async function getMemphisOrderInfo({ storeId, memphisOrderId }) {
  const res = await apolloClient.query({
    query: Schemas.GET_MEMPHIS_ORDER_INFO,
    variables: { storeId, orderNumber: memphisOrderId },
  });
  if (res.data.getMemphisOrderInfo.code === 200) {
    return { success: true, data: res.data.getMemphisOrderInfo.info };
  }
  if (
    res.data.getMemphisOrderInfo.code === 404 ||
    res.data.getMemphisOrderInfo.code === 400
  ) {
    return { success: false, message: res.data.getMemphisOrderInfo.message };
  }
  return null;
}

/**
 * @param {String} externalOrderCode
 * @param {String} storeId
 * @returns {Promise<string>} link
 */
async function getOrderLink({ externalOrderCode, storeId }) {
  const res = await apolloClient.query({
    query: Schemas.GET_ORDER_LINK,
    variables: { externalOrderCode, storeId },
  });
  if (res.data.getOrderLink.code === 200) {
    return res.data.getOrderLink.link;
  }
  return null;
}

export default {
  createClient,
  getClientId,
  getDevolutionPageData,
  getVtexOrderInfo,
  createOrder,
  getAddressByZipCode,
  setOrderReturnMethod,
  approveOrRejectOrder,
  getStoreIdByNameId,
  getMemphisOrderInfo,
  createOrderWithClientInfo,
  getOrderLink,
};
